import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useGlimmerTheme } from '../../theme';

const Wrapper = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ChallengeNumber = styled.div<{
  size: 'sm' | 'md';
  bgColor: string;
  color: string;
}>`
  font-size: ${({ theme, size }) =>
    size === 'md' ? theme.sizes.caption : theme.sizes.xmedium};
  font-family: ${({ theme }) => theme.typography.rubikSemibold};
  font-weight: ${({ theme }) => theme.typography.weight.bold};
  width: ${({ size }) => (size === 'md' ? '40px' : '32px')};
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${({ size }) => (size === 'md' ? '40px' : '32px')};
  background-color: ${({ bgColor }) => bgColor};
  color: ${({ color }) => color};
  border-radius: 4px;

  svg,
  path {
    fill: ${({ color }) => color};
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const TitleWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.tokens.color.global.neutral.neutral700.value};
`;

const Heading = styled('span')<{ size: 'sm' | 'md'; color: string }>`
  font-size: ${({ theme, size }) =>
    size === 'md' ? theme.sizes.xsmall : theme.sizes.micro};
  letter-spacing: 0.115em;
  line-height: ${({ size }) => (size === 'md' ? '20px' : '15px')};
  text-transform: uppercase;
  font-weight: ${({ theme }) => theme.typography.weight.bold};
  color: ${({ color }) => color};
  font-family: ${({ theme }) => theme.typography.medium};
`;

const Title = styled('span')<{ size: 'sm' | 'md' }>`
  ${({ size, theme }) =>
    size === 'md'
      ? css`
          color: ${theme.tokens.color.global.neutral.neutral700.value};
          font-size: ${theme.sizes.xmedium};
          font-family: ${theme.typography.medium};
          font-weight: ${theme.typography.weight.medium};
          @media (max-width: ${theme.breakpoints.lg}) {
            font-size: ${theme.sizes.xmedium};
          }
        `
      : css`
          color: ${theme.tokens.color.global.neutral.neutral700.value};
          font-size: ${theme.sizes.small};
          font-family: ${theme.typography.regular};
          font-weight: ${theme.typography.weight.regular};
          @media (max-width: ${theme.breakpoints.lg}) {
            font-size: ${theme.sizes.xsmall};
          }
        `}

  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
`;

type ChallengeTitleProps = {
  challengeBoxChildren?: JSX.Element;
  title?: JSX.Element | string;
  children?: JSX.Element | string;
  size: 'sm' | 'md';
  challengeTitle: JSX.Element | string | undefined | null;
  bgColor?: string;
  color?: string;
} & React.HTMLAttributes<HTMLDivElement>;

function ChallengeTitle({
  challengeBoxChildren,
  children,
  title,
  size,
  challengeTitle,
  bgColor,
  color,
  ...props
}: ChallengeTitleProps): JSX.Element {
  const theme = useGlimmerTheme();

  const defaultBgColor =
    bgColor || theme.tokens.color.global.teal.teal300.value;
  const defaultColor = color || theme.tokens.color.global.teal.teal700.value;

  return (
    <Wrapper {...props}>
      {challengeBoxChildren && (
        <ChallengeNumber
          color={defaultColor}
          bgColor={defaultBgColor}
          size={size}
        >
          {challengeBoxChildren}
        </ChallengeNumber>
      )}

      <TitleWrapper>
        <Heading color={defaultColor} size={size}>
          {challengeTitle}
        </Heading>
        {title ? <Title size={size}>{title}</Title> : children}
      </TitleWrapper>
    </Wrapper>
  );
}

export { ChallengeTitle };
export type { ChallengeTitleProps };
